import React, { useState, useRef, useEffect, useCallback } from 'react';
import { callGemini, parseTemplateResponse } from '../services/geminiService';
import { extractContent, SUPPORTED_FILE_TYPES } from '../utils/docParser';
import ipc from '../services/ipc';
import './AITemplateBuilder.css';

// =============================================================================
// AITemplateBuilder — two-pane modal: chat + live JSON preview
// =============================================================================

export default function AITemplateBuilder({ onTemplateReady, onClose, initialTemplate, draftId: initialDraftId }) {
  // -- State ----------------------------------------------------------------
  const [messages, setMessages] = useState([]);        // { role, text, fileName? }
  const [inputText, setInputText] = useState('');
  const [pendingFile, setPendingFile] = useState(null); // File object
  const [isLoading, setIsLoading] = useState(false);
  const [template, setTemplate] = useState(initialTemplate || null);
  const [error, setError] = useState(null);

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  // Stable draft ID for this builder session — reuse existing or create new
  const draftIdRef = useRef(initialDraftId || `draft_${Date.now()}`);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  // -- Send message to Gemini -----------------------------------------------

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    const file = pendingFile;

    if (!text && !file) return;

    setIsLoading(true);
    setError(null);

    // Build the user message
    const userMsg = { role: 'user', text: '', fileName: null, imageBase64: null, imageMime: null };

    try {
      // Process file if attached
      if (file) {
        userMsg.fileName = file.name;
        const extracted = await extractContent(file);
        if (extracted.type === 'image') {
          userMsg.imageBase64 = extracted.imageBase64;
          userMsg.imageMime = extracted.imageMime;
          userMsg.text = text || `Please analyze this image and generate a process template from it.`;
        } else {
          // Text-based document — prepend extracted text to the user message
          const docText = extracted.text;
          const truncated = docText.length > 15000 ? docText.slice(0, 15000) + '\n\n[...document truncated at 15,000 chars]' : docText;
          userMsg.text = text
            ? `${text}\n\n--- Uploaded document: ${file.name} ---\n${truncated}`
            : `Here is a document describing a process. Please generate a LiTeCAB template from it.\n\n--- ${file.name} ---\n${truncated}`;
        }
      } else {
        userMsg.text = text;
      }

      // If we already have a template from a previous turn, include it for context
      if (template && !userMsg.text.includes('"project_name"')) {
        userMsg.text += `\n\nCurrent template (update this based on my request):\n${JSON.stringify(template, null, 2)}`;
      }

      // Add user message to chat
      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setInputText('');
      setPendingFile(null);

      // Call Gemini
      const responseText = await callGemini(updatedMessages);

      // Try to parse as template JSON
      let parsed = null;
      let assistantText = '';
      try {
        parsed = parseTemplateResponse(responseText);
        setTemplate(parsed);
        const processCount = Object.keys(parsed.processes).length;
        assistantText = `Generated template: "${parsed.project_name}" with ${processCount} process${processCount !== 1 ? 'es' : ''}. You can ask me to modify it, or click "Load into Tool" to start your analysis.`;
        // Persist draft to IDB so it survives modal close
        ipc.invoke('save-ai-draft', draftIdRef.current, parsed).catch(() => {});
      } catch {
        // Response wasn't valid JSON — show it as a plain message
        assistantText = responseText;
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: assistantText },
      ]);
    } catch (err) {
      console.error('[AITemplateBuilder] Gemini error:', err);
      setError(err.message);
      setMessages((prev) => [
        ...prev,
        { role: 'error', text: `Error: ${err.message}` },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [inputText, pendingFile, messages, template]);

  // -- Key handler ----------------------------------------------------------

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // -- File upload ----------------------------------------------------------

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = '';
  };

  // -- Load into tool -------------------------------------------------------

  const handleLoadIntoTool = () => {
    if (!template) return;

    // Build the project object matching LiTeCAB's expected structure
    const processes = {};
    const srcProcesses = template.processes || {};

    Object.entries(srcProcesses).forEach(([pid, proc]) => {
      processes[pid] = {
        id: proc.id || pid,
        name: proc.name || 'Unnamed_Process',
        process_type: proc.process_type || 'unit_process',
        manufacturing_category: proc.manufacturing_category || 'Custom',
        hierarchy_level: proc.hierarchy_level ?? 0,
        is_side_input: proc.is_side_input ?? false,
        energy_input_kwh: proc.energy_input_kwh ?? 0,
        equipment: proc.equipment || '',
        parameters: proc.parameters || { duration_hours: 1, temperature_c: 25 },
        next_process_ids: proc.next_process_ids || [],
        description: proc.description || '',
        products: proc.products || [],
        lci_inputs: (proc.lci_inputs || []).map((inp) => ({
          name: inp.name || 'Unknown Input',
          quantity: inp.quantity ?? 0,
          unit: inp.unit || 'kg',
          cost_per_unit: inp.cost_per_unit ?? null,
          cost_unit: inp.cost_unit || null,
        })),
      };
    });

    const project = {
      project_name: template.project_name,
      project_info: {
        name: template.project_name,
        description: template.description || 'AI-generated template',
        chemistry: 'Custom',
        category: template.functional_unit?.scale || 'Custom',
        battery_modules: 1,
        scaling_factor: 1.0,
        created: new Date().toISOString(),
      },
      functional_unit: template.functional_unit || {},
      processes,
      analysis_results: { lca: null, tea: null },
    };

    onTemplateReady(project);
  };

  // -- Download template JSON -----------------------------------------------

  const handleDownloadTemplate = () => {
    if (!template) return;
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(template.project_name || 'template').replace(/\s+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // -- Render ---------------------------------------------------------------

  return (
    <div className="ai-builder-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ai-builder-modal">
        {/* Header */}
        <div className="ai-builder-header">
          <h2>Create Your Own Template</h2>
          <div className="ai-builder-header-actions">
            <button className="ai-builder-close" onClick={onClose} title="Close">
              &times;
            </button>
          </div>
        </div>

        {/* Body: two panes */}
        <div className="ai-builder-body">
          {/* Left pane — Chat */}
          <div className="ai-chat-pane">
            <div className="ai-chat-messages">
              {messages.length === 0 && !isLoading && (
                <div className="ai-chat-welcome">
                  <h3>Describe your process</h3>
                  <p>Type a description, paste process details, or upload a document (PDF, DOCX) or image (screenshot, diagram).</p>
                  <p style={{ marginTop: '8px', fontSize: '0.85em', opacity: 0.6 }}>
                    Supported: {SUPPORTED_FILE_TYPES}
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`ai-msg ${msg.role}`}>
                  {msg.fileName && (
                    <div className="file-tag">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                      {msg.fileName}
                    </div>
                  )}
                  {msg.text && <div>{msg.text}</div>}
                </div>
              ))}

              {isLoading && (
                <div className="ai-msg-thinking">
                  <div className="ai-thinking-dots">
                    <span /><span /><span />
                  </div>
                  Generating template...
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Chat input */}
            <div className="ai-chat-input-area">
              {pendingFile && (
                <div className="ai-file-pending">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                  {pendingFile.name}
                  <button onClick={() => setPendingFile(null)}>&times;</button>
                </div>
              )}

              <div className="ai-chat-input-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <button
                  className="ai-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title={`Upload file (${SUPPORTED_FILE_TYPES})`}
                  disabled={isLoading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>

                <textarea
                  ref={textareaRef}
                  className="ai-chat-textarea"
                  placeholder={messages.length === 0
                    ? 'Describe your process, e.g. "NMC811 cathode synthesis via co-precipitation at pilot scale..."'
                    : 'Ask to modify the template...'}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isLoading}
                />

                <button
                  className="ai-send-btn"
                  onClick={handleSend}
                  disabled={isLoading || (!inputText.trim() && !pendingFile)}
                >
                  {isLoading ? 'Generating...' : 'Send'}
                </button>
              </div>
            </div>
          </div>

          {/* Right pane — JSON Preview */}
          <div className="ai-preview-pane">
            <div className="ai-preview-header">
              <span>Template Preview</span>
              {template && (
                <span style={{ color: '#4fd1c5', fontSize: '0.8em' }}>
                  {Object.keys(template.processes || {}).length} processes
                </span>
              )}
            </div>
            <div className="ai-preview-body">
              {template ? (
                <pre>{JSON.stringify(template, null, 2)}</pre>
              ) : (
                <div className="ai-preview-empty">
                  Template JSON will appear here after generation
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="ai-builder-footer">
          <button
            className="ai-download-btn"
            onClick={handleDownloadTemplate}
            disabled={!template}
          >
            Download JSON
          </button>
          <button
            className="ai-load-btn"
            onClick={handleLoadIntoTool}
            disabled={!template}
          >
            Load into Tool
          </button>
        </div>
      </div>
    </div>
  );
}
